import { User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { app, functions } from "./firebase";

const TAG = "[web-push]";
const STORAGE_KEY = "zca.webPushToken";

function log(...args: unknown[]) {
  // eslint-disable-next-line no-console
  console.log(TAG, ...args);
}

function buildServiceWorkerUrl(): string {
  const params = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  });

  return `/firebase-messaging-sw.js?${params.toString()}`;
}

function buildClickUrl(data: Record<string, string | undefined>): string {
  const threadId = data.threadId || data.threadid;
  const newsId = data.newsId || data.newsid;
  const clickUrl = data.clickUrl || data.clickurl;

  if (clickUrl) return clickUrl;
  if (threadId) return `/messaging?threadId=${encodeURIComponent(threadId)}`;
  if (newsId) return `/news?id=${encodeURIComponent(newsId)}`;
  return "/messaging";
}

export async function clearWebPushToken(): Promise<void> {
  const existing = localStorage.getItem(STORAGE_KEY);
  if (!existing) return;

  try {
    const unregister = httpsCallable(functions, "unregisterPushToken");
    await unregister({ token: existing });
    log("Cleared browser push token.");
  } catch (e) {
    console.warn(TAG, "Failed to clear browser push token.", e);
  } finally {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export async function ensureWebPushRegistration(user: User): Promise<() => void> {
  if (!user?.uid) {
    await clearWebPushToken();
    return () => {};
  }

  if (!(await isSupported())) {
    log("Firebase web messaging is not supported in this browser.");
    return () => {};
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
  if (!vapidKey) {
    console.warn(TAG, "Missing VITE_FIREBASE_VAPID_KEY.");
    return () => {};
  }

  log(`Notification.permission: ${Notification.permission}`);

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
    log(`Notification permission after prompt: ${permission}`);
  }

  if (permission !== "granted") {
    log("Notification permission not granted; skipping browser push registration.");
    return () => {};
  }

  const messaging = getMessaging(app);
  const existingToken = localStorage.getItem(STORAGE_KEY);
  if (existingToken) {
    try {
      const unregister = httpsCallable(functions, "unregisterPushToken");
      await unregister({ token: existingToken });
    } catch (e) {
      console.warn(TAG, "Failed to unregister previous browser push token before refresh.", e);
    }

    try {
      await deleteToken(messaging);
      log("Deleted cached browser FCM token before refresh.");
    } catch (e) {
      console.warn(TAG, "Failed to delete cached browser FCM token before refresh.", e);
    } finally {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  const swReg = await navigator.serviceWorker.register(buildServiceWorkerUrl());
  log(`Service worker registered: ${swReg.scope}`);

  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: swReg,
  });

  if (!token) {
    log("No browser FCM token returned.");
    return () => {};
  }

  log("Browser FCM token acquired.");

  const register = httpsCallable(functions, "registerPushToken");
  await register({
    token,
    provider: "fcm-web",
    platform: "web",
    userAgent: navigator.userAgent,
  });

  localStorage.setItem(STORAGE_KEY, token);
  log("Browser FCM token stored via callable function.");

  const unsubscribeForeground = onMessage(messaging, (payload) => {
    log("Foreground message received.", payload);

    const title = payload.notification?.title || payload.data?.title || "ZCA";
    const body = payload.notification?.body || payload.data?.body || "You have a new notification.";
    const clickUrl = buildClickUrl((payload.data ?? {}) as Record<string, string | undefined>);

    navigator.serviceWorker.ready
      .then((registration) =>
        registration.showNotification(title, {
          body,
          icon: "/notification-icon.png",
          badge: "/notification-icon.png",
          data: { clickUrl },
        })
      )
      .catch((err) => {
        console.warn(TAG, "Service worker foreground notification failed.", err);
        const n = new Notification(title, {
        body,
        icon: "/notification-icon.png",
        data: { clickUrl },
      });
        n.onclick = () => {
          window.focus();
          window.location.assign(clickUrl);
          n.close();
        };
      });
  });

  return () => {
    unsubscribeForeground();
  };
}
